import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import Avatar from "flarum/common/components/Avatar";
import humanTime from "flarum/common/helpers/humanTime";
import username from "flarum/common/helpers/username";
import classList from "flarum/common/utils/classList";
import { MessageStreamSkeleton } from "./Skeletons";
import { messagePreview } from "../../common/utils/preview";
import { authorAvatar, authorName } from "../utils/bot";
/**
 * The chat's moderation queue.
 *
 * flarum/flags cannot back this: its `flags.post_id` is a non-nullable foreign key
 * into `posts`, so a chat message id is refused by the database. Rather than alter
 * another extension's schema, the chat keeps its own reports — same shape, same
 * two decisions at the end of each one: the message stays, or it goes.
 *
 * Rows are summaries, as in BookmarksList: a full ChatMessage draws reply, edit and
 * pin actions from its own capability flags, and none of those are what a moderator
 * came here to do.
 */
export default class FlaggedMessagesList extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "flags", []);
    _defineProperty(this, "loading", true);
    /** Id of the flag whose row is mid-request, so only that row shows a spinner. */
    _defineProperty(this, "working", null);
    /** Whether resolved reports are shown alongside the open ones. */
    _defineProperty(this, "showResolved", false);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }
  view() {
    // Reading the queue is `ramon-chat.moderate`. The route is reachable by
    // anyone who may use the chat, so the component says no rather than showing
    // an empty list that looks like "no reports".
    if (!app.forum.attribute("canModerateChat")) {
      return m("div", {
        className: "ChatFlags"
      }, m("div", {
        className: "ChatBrowse-empty"
      }, app.translator.trans("ramon-chat.forum.flags.forbidden")));
    }
    return m("div", {
      className: "ChatFlags"
    }, m("div", {
      className: "ChatFlags-header"
    }, m("h3", {
      className: "ChatFlags-title"
    }, app.translator.trans("ramon-chat.forum.flags.title")), m(Button, {
      className: classList("Button Button--flat ChatFlags-toggle", {
        "ChatFlags-toggle--active": this.showResolved
      }),
      icon: this.showResolved ? "fas fa-eye" : "far fa-eye",
      onclick: () => {
        this.showResolved = !this.showResolved;
        this.load();
      }
    }, app.translator.trans("ramon-chat.forum.flags.show_resolved"))), this.body());
  }
  body() {
    if (this.loading) return MessageStreamSkeleton(4);
    if (this.flags.length === 0) {
      return m("div", {
        className: "ChatBrowse-empty"
      }, app.translator.trans(this.showResolved ? "ramon-chat.forum.flags.empty_all" : "ramon-chat.forum.flags.empty"));
    }
    return this.flags.map(flag => this.row(flag));
  }
  row(flag) {
    const message = flag.message();
    const id = Number(flag.id());
    const resolved = Boolean(flag.isResolved());
    const at = flag.createdAt();
    return m("div", {
      className: classList("ChatFlags-row", {
        "ChatFlags-row--resolved": resolved
      }),
      key: flag.id()
    }, m("div", {
      className: "ChatFlags-reporter"
    }, m(Avatar, {
      user: flag.user(),
      className: "Avatar"
    }), m("div", {
      className: "ChatFlags-reporter-meta"
    }, m("span", {
      className: "ChatFlags-reporter-name"
    }, username(flag.user())), at ? m("span", {
      className: "ChatFlags-time"
    }, humanTime(at)) : null), m("span", {
      className: "ChatFlags-reason ChatFlags-reason--".concat(flag.reason())
    }, app.translator.trans("ramon-chat.forum.flag.reasons.".concat(flag.reason())))), flag.detail() ? m("div", {
      className: "ChatFlags-detail"
    }, flag.detail()) : null, message ? this.target(message) : m("div", {
      className: "ChatFlags-gone"
    }, app.translator.trans("ramon-chat.forum.flags.message_gone")), m("div", {
      className: "ChatFlags-actions"
    }, message ? m(Button, {
      className: "Button Button--flat",
      icon: "fas fa-arrow-right",
      onclick: () => this.open(message)
    }, app.translator.trans("ramon-chat.forum.flags.go_to")) : null, resolved ? m("span", {
      className: "ChatFlags-resolvedBy"
    }, app.translator.trans("ramon-chat.forum.flags.resolved_by", {
      // Not `user`: the translator reserves that key, extracts it and
      // runs username() on it itself — which crashed on the vnode this
      // already is.
      moderator: username(flag.resolvedBy())
    })) : [message && !message.isDeleted() && message.canDelete() ? m(Button, {
      key: "delete",
      className: "Button Button--flat ChatFlags-delete",
      icon: "fas fa-trash",
      loading: this.working === id,
      onclick: () => this.deleteMessage(flag, message)
    }, app.translator.trans("ramon-chat.forum.flags.delete_message")) : null, m(Button, {
      key: "resolve",
      className: "Button Button--flat",
      icon: "fas fa-check",
      loading: this.working === id,
      onclick: () => this.resolve(flag)
    }, app.translator.trans("ramon-chat.forum.flags.dismiss"))]));
  }
  target(message) {
    const channel = this.attrs.state.channel(message.channelId());
    return m("div", {
      className: "ChatFlags-target"
    }, m("div", {
      className: "ChatFlags-target-meta"
    }, authorAvatar(message, "Avatar Avatar--small"), m("span", {
      className: "ChatFlags-target-author"
    }, authorName(message)), channel ? m("span", {
      className: "ChatFlags-target-channel"
    }, channel.displayName()) : null), m("div", {
      className: "ChatFlags-target-excerpt"
    }, message.isDeleted() ? app.translator.trans("ramon-chat.forum.flags.message_deleted") : messagePreview(message, 240)));
  }
  async load() {
    this.loading = true;
    m.redraw();
    try {
      const results = await app.store.find("chat-message-flags", {
        // Always stated, never omitted. The server applies no default of its own —
        // it cannot, because a default in the searcher's query could not be undone
        // by a filter that runs after it — so a bare listing would return the
        // history too. Sending it either way keeps the two views honest.
        //
        // A `filter[...]` rather than a plain query parameter because Flarum 2
        // rejects any parameter it does not recognise.
        filter: {
          resolved: this.showResolved ? "1" : "0"
        },
        sort: "-createdAt",
        page: {
          limit: 50
        }
      });
      this.flags = Array.isArray(results) ? results : [];
    } catch (_unused) {
      this.flags = [];
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.flags.load_failed"));
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  /**
   * Opens the channel the reported message lives in, and its thread when it has
   * one — a thread reply is not in the channel window, so routing to the channel
   * alone would land somewhere the message is not.
   */
  open(message) {
    const channelId = message.channelId();
    if (!channelId) return;
    const threadId = message.threadId();
    this.attrs.state.setActiveChannel(channelId);
    this.attrs.state.activeThreadId = threadId != null ? threadId : null;
    m.route.set(threadId ? app.route("chat.thread", {
      id: channelId,
      threadId
    }) : app.route("chat.channel", {
      id: channelId
    }));
  }
  async resolve(flag) {
    const id = Number(flag.id());
    this.working = id;
    m.redraw();
    try {
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-message-flags/").concat(id, "/resolve")
      });
      this.afterResolved(flag);
    } catch (_unused2) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.flags.action_failed"));
    } finally {
      this.working = null;
      m.redraw();
    }
  }

  /**
   * Deletes the reported message. The server closes every open report about it in
   * the same breath — see ResolveFlagsOnModeration — so this row is done too.
   */
  async deleteMessage(flag, message) {
    if (!confirm(app.translator.trans("ramon-chat.forum.message.delete_confirm", {}, true))) return;
    const id = Number(flag.id());
    this.working = id;
    m.redraw();
    try {
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(message.id(), "/delete")
      });

      // Always a moderator removal: this is the moderation queue, and the
      // author cannot reach it.
      message.pushAttributes({
        isDeleted: true,
        content: null,
        contentHtml: null,
        isModeratorDeleted: true
      });
      this.afterResolved(flag);
    } catch (_unused3) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.message.delete_failed"));
    } finally {
      this.working = null;
      m.redraw();
    }
  }

  /**
   * Drops the row from the open queue, or marks it in place when resolved reports
   * are on screen — removing it there would hide the very thing being shown.
   */
  afterResolved(flag) {
    var _app$forum$attribute;
    flag.pushAttributes({
      isResolved: true,
      resolvedAt: new Date()
    });
    if (!this.showResolved) {
      this.flags = this.flags.filter(f => f.id() !== flag.id());
    }

    // Keep the sidebar badge honest without a round trip.
    const count = Number((_app$forum$attribute = app.forum.attribute("chatOpenFlagsCount")) != null ? _app$forum$attribute : 0);
    app.forum.pushAttributes({
      chatOpenFlagsCount: Math.max(0, count - 1)
    });
  }
}
flarum.reg.add('ramon-chat', 'forum/components/FlaggedMessagesList', FlaggedMessagesList);